/**
 * Embed-only host page content: consent, capture via loader, summarize API, and chat send payload.
 */
import { BASE_PATH, getAccessToken } from "../api/api";
import { embedRequestHeaders, isEmbedRuntime } from "./embedRuntime";
import type { PageTextPayload } from "../embed-shared/protocol";

const API_BASE = BASE_PATH;

export type PageSummary = {
  summary: string;
  keywords: string[];
  /** Important page facts extracted as short key → value pairs. */
  fields: Record<string, string>;
  pageUrl: string;
  contentHash: string;
  consentGiven: true;
};

type ConsentRecord = { pageUrl: string; correlationId: string; grantedAt: string };

const PAGE_INTENT =
  /\b(bu\s+sayfa|bu\s+ekran|bu\s+sayfada|bu\s+ekranda|sayfadaki|ekrandaki|buradaki|this\s+page|this\s+screen|on\s+this\s+page|current\s+page|what('s|\s+is)\s+on\s+(the\s+)?(page|screen)|sayfa\s+hakkında|ekran\s+hakkında)\b/i;

let requestPageTextFn: (() => Promise<PageTextPayload>) | null = null;
let lastPageUrl: string | null = null;
let cachedSummary: PageSummary | null = null;

export function setPageTextRequester(fn: (() => Promise<PageTextPayload>) | null): void {
  requestPageTextFn = fn;
}

export function setEmbedPageUrl(pageUrl: string | null): void {
  if (pageUrl && lastPageUrl && pageUrl !== lastPageUrl) {
    cachedSummary = null;
  }
  lastPageUrl = pageUrl;
}

export function embedPageUrl(): string | null {
  return lastPageUrl;
}

export function looksLikePageIntent(message: string): boolean {
  return PAGE_INTENT.test((message || "").trim());
}

function consentStorageKey(embedKey: string, pageUrl: string): string {
  return `gar-embed-page-consent:${embedKey}:${pageUrl}`;
}

export function hasPageContentConsent(embedKey: string, pageUrl: string, correlationId: string): boolean {
  try {
    const raw = sessionStorage.getItem(consentStorageKey(embedKey, pageUrl));
    if (!raw) return false;
    const rec = JSON.parse(raw) as ConsentRecord;
    return rec.pageUrl === pageUrl && rec.correlationId === correlationId;
  } catch {
    return false;
  }
}

export function grantPageContentConsent(embedKey: string, pageUrl: string, correlationId: string): void {
  const rec: ConsentRecord = { pageUrl, correlationId, grantedAt: new Date().toISOString() };
  sessionStorage.setItem(consentStorageKey(embedKey, pageUrl), JSON.stringify(rec));
}

export function clearPageContentConsent(embedKey: string, pageUrl: string): void {
  sessionStorage.removeItem(consentStorageKey(embedKey, pageUrl));
  if (cachedSummary?.pageUrl === pageUrl) cachedSummary = null;
}

export async function requestHostPageText(): Promise<PageTextPayload> {
  if (!requestPageTextFn) throw new Error("Page text capture is not available");
  return requestPageTextFn();
}

export async function summarizeHostPage(
  payload: PageTextPayload,
  locale?: string
): Promise<PageSummary> {
  const token = await getAccessToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_BASE}/api/embed/page-content/summarize`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...embedRequestHeaders(),
    },
    body: JSON.stringify({
      pageUrl: payload.pageUrl,
      title: payload.title,
      text: payload.text,
      consentGiven: true,
      locale,
    }),
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Summarize failed: ${res.status} ${errText || res.statusText}`);
  }
  const data = (await res.json()) as {
    summary: string;
    keywords?: string[];
    fields?: Record<string, unknown>;
    pageUrl: string;
    contentHash: string;
  };
  const fields: Record<string, string> = {};
  if (data.fields && typeof data.fields === "object" && !Array.isArray(data.fields)) {
    for (const [key, value] of Object.entries(data.fields)) {
      if (value == null) continue;
      const k = String(key).trim().slice(0, 64);
      const v = String(value).trim().slice(0, 300);
      if (k && v) fields[k] = v;
    }
  }
  const summary: PageSummary = {
    summary: data.summary,
    keywords: Array.isArray(data.keywords) ? data.keywords : [],
    fields,
    pageUrl: data.pageUrl,
    contentHash: data.contentHash,
    consentGiven: true,
  };
  cachedSummary = summary;
  return summary;
}

/** Summary to attach to chat/send when consent was granted for this page in this conversation. */
export function embedPageSummaryForSend(embedKey: string, correlationId: string): PageSummary | undefined {
  if (!isEmbedRuntime() || !cachedSummary) return undefined;
  if (!hasPageContentConsent(embedKey, cachedSummary.pageUrl, correlationId)) return undefined;
  return cachedSummary;
}

export function clearCachedPageSummary(): void {
  cachedSummary = null;
}
